import { useCallback } from 'react';
import type { StoryPhoto } from '../components/StoryGallery';

type NarrationToggleOptions = {
  isNarrationSupported: boolean;
  narrationEnabled: boolean;
  activePhotos: StoryPhoto[];
  currentPhoto: StoryPhoto | null;
  unlockAudio: () => void;
  setNarrationStartPending: (value: boolean) => void;
  mutedRef: React.MutableRefObject<boolean>;
  setIsMuted: (value: boolean) => void;
  fadeOutStartRef: React.MutableRefObject<number | null>;
  setLastSpokenKey: (value: string | null) => void;
  setResetSignal: React.Dispatch<React.SetStateAction<number>>;
  setRequestedPhotoIndex: (value: number | null) => void;
  setActivePhotoIndex: (value: number) => void;
  setCurrentPhoto: (value: StoryPhoto | null) => void;
  setFirstSequenceKickEnabled: (value: boolean) => void;
  setNarrationEnabled: (value: boolean) => void;
  firstSequenceKickTimerRef: React.MutableRefObject<number | null>;
  narrationEnabledRef: React.MutableRefObject<boolean>;
  activeIndexRef: React.MutableRefObject<number>;
  activePhotoIndexRef: React.MutableRefObject<number>;
  activeIndex: number;
  activePhotoIndex: number;
  handleNarrationAdvance: () => void;
};

export const useNarrationToggle = ({
  isNarrationSupported,
  narrationEnabled,
  activePhotos,
  currentPhoto,
  unlockAudio,
  setNarrationStartPending,
  mutedRef,
  setIsMuted,
  fadeOutStartRef,
  setLastSpokenKey,
  setResetSignal,
  setRequestedPhotoIndex,
  setActivePhotoIndex,
  setCurrentPhoto,
  setFirstSequenceKickEnabled,
  setNarrationEnabled,
  firstSequenceKickTimerRef,
  narrationEnabledRef,
  activeIndexRef,
  activePhotoIndexRef,
  activeIndex,
  activePhotoIndex,
  handleNarrationAdvance,
}: NarrationToggleOptions) => {
  return useCallback(() => {
    if (!isNarrationSupported) return;
    if (!narrationEnabled) {
      const startPhoto = activePhotos[0] ?? currentPhoto;
      unlockAudio();
      setNarrationStartPending(true);
      mutedRef.current = false;
      setIsMuted(false);
      fadeOutStartRef.current = null;
      setLastSpokenKey(null);
      setResetSignal((prev) => prev + 1);
      setRequestedPhotoIndex(0);
      setActivePhotoIndex(0);
      if (startPhoto) {
        setCurrentPhoto(startPhoto);
      }
      setFirstSequenceKickEnabled(activeIndex === 0 && activePhotoIndex === 0);
      setNarrationEnabled(true);
      if (activeIndex === 0 && activePhotoIndex === 0) {
        if (firstSequenceKickTimerRef.current !== null) {
          window.clearTimeout(firstSequenceKickTimerRef.current);
        }
        firstSequenceKickTimerRef.current = window.setTimeout(() => {
          if (!narrationEnabledRef.current) return;
          if (activeIndexRef.current === 0 && activePhotoIndexRef.current === 0) {
            handleNarrationAdvance();
          }
          setFirstSequenceKickEnabled(false);
          firstSequenceKickTimerRef.current = null;
        }, 25000);
      }
      return;
    }
    mutedRef.current = true;
    setIsMuted(true);
    setNarrationEnabled(false);
    if (firstSequenceKickTimerRef.current !== null) {
      window.clearTimeout(firstSequenceKickTimerRef.current);
      firstSequenceKickTimerRef.current = null;
    }
    setFirstSequenceKickEnabled(false);
  }, [
    isNarrationSupported,
    narrationEnabled,
    activePhotos,
    currentPhoto,
    unlockAudio,
    setNarrationStartPending,
    mutedRef,
    setIsMuted,
    fadeOutStartRef,
    setLastSpokenKey,
    setResetSignal,
    setRequestedPhotoIndex,
    setActivePhotoIndex,
    setCurrentPhoto,
    setFirstSequenceKickEnabled,
    setNarrationEnabled,
    firstSequenceKickTimerRef,
    narrationEnabledRef,
    activeIndexRef,
    activePhotoIndexRef,
    activeIndex,
    activePhotoIndex,
    handleNarrationAdvance,
  ]);
};
